// App Router /////////////////////////////////////////////////////////////////
//
// This is the code that parses input URL's for their query parameters.
//
// App -> App Router
///////////////////////////////////////////////////////////////////////////////

// SETUP //////////////////////////////////////////////////////////////////////
// Third-party imports.
import PropTypes from 'prop-types'
import React, { Suspense, } from 'react'

// Local imports.
import { ProgramPortalTypes } from './types/portal'
import ErrorBoundary from './components/ErrorBoundary'
import PortalErrorBoundary from './components/Portal/PortalErrorBoundary'
import useLocationObj from './hooks/useLocationObj'

// HELPER FUNCTIONS ///////////////////////////////////////////////////////////


// MAIN FUNCTION //////////////////////////////////////////////////////////////
interface QueryRouterProps {
  detailComponent      : React.ReactNode,
  searchComponent      : React.ReactNode,
  equityComponent      : React.ReactNode,
  equityDetailComponent: React.ReactNode,
  formComponent        : React.ReactNode,
  portalComponent      : React.ReactNode, // must be a React.Lazy
}

const QueryRouter: React.FC<QueryRouterProps> = ({
  detailComponent = null,
  searchComponent = null,
  equityComponent = null,
  equityDetailComponent = null,
  formComponent = null,
  portalComponent = null,
}) => {
  /* 
     on first render, search is undefined
     page will re-render when search is populated.

     it should never be the case that the components
     are null or undefined. but just in case, provide
     a message as a fallback
  */
  const { search, pathname, } = useLocationObj()
  let children = null
  let ErrorBoundaryComponent = ErrorBoundary

  // Determine the children based on the query parameters provided.
  if (search && search.es && search.id) 
    children = equityDetailComponent ? equityDetailComponent : <span>Equity Support detail is unavailable</span>
  
  else if (search && search.es) 
    children = equityComponent ? equityComponent : <span>Equity Support search is unavailable</span>
  
  else if (search && search.newprogram) 
    children = formComponent ? formComponent : <span>Form is unavailable</span>
  
  else if (search && search.portal && portalComponent) {
    const Portal = portalComponent as React.FC<ProgramPortalTypes.Props>
    children = <Suspense fallback={<div>Loading...</div>}><Portal search={search} pathname={pathname}/></Suspense>
    ErrorBoundaryComponent = PortalErrorBoundary
  }

  else if (search && search.type && search.id) 
    children = detailComponent ? detailComponent : <span>Program Details are unavailable</span>
  
  else 
    children = searchComponent ? searchComponent : <span>Program Search is unavailable</span>
  
  // RETURN HTML //////////////////////////////////////////////////////////////
  return <ErrorBoundaryComponent>
    {children}
  </ErrorBoundaryComponent>
}

QueryRouter.propTypes = {
  detailComponent      : PropTypes.node,
  searchComponent      : PropTypes.node,
  equityComponent      : PropTypes.node,
  equityDetailComponent: PropTypes.node,
  formComponent        : PropTypes.node,
  portalComponent      : PropTypes.oneOfType([ PropTypes.func, PropTypes.object, ]), // must be a React.Lazy
}

export default QueryRouter